/* oxlint-disable react/only-export-components -- context module exports hooks + provider */
import { type ReactNode, createContext, useContext, useState } from "react";

import { useEditorState } from "@tiptap/react";

import { useEditorContext } from "../../editor/EditorContext";
import {
  shouldShowFileSection,
  shouldShowLinkSection,
  shouldShowTableSection,
} from "./predicates";

export type FloatingToolbarFeatures = {
  link: boolean;
  file: boolean;
  table: boolean;
};

export type FloatingToolbarMode =
  | { type: "idle" }
  | { type: "editLink"; draft: string }
  | { type: "editFile"; draft: string }
  | { type: "tableMenu" };

export type FloatingToolbarContextValue = {
  features: FloatingToolbarFeatures;
  mode: FloatingToolbarMode;
  idle: () => void;
  startEditLink: (href: string) => void;
  setLinkDraft: (draft: string) => void;
  startEditFile: (name: string) => void;
  setFileDraft: (draft: string) => void;
  openTableMenu: () => void;
  closeTableMenu: () => void;
};

const IDLE: FloatingToolbarMode = { type: "idle" };

const FloatingToolbarContext =
  createContext<FloatingToolbarContextValue | null>(null);

export const FloatingToolbarProvider = ({
  features,
  children,
}: {
  features: FloatingToolbarFeatures;
  children: ReactNode;
}) => {
  const { editor } = useEditorContext();
  const [mode, setMode] = useState<FloatingToolbarMode>(IDLE);

  const selection = useEditorState({
    editor,
    selector: ({ editor: instance }) => {
      if (!instance || instance.isDestroyed) {
        return { link: false, file: false, table: false };
      }
      return {
        link: shouldShowLinkSection(instance, instance.state, {
          requireFocus: false,
        }),
        file: shouldShowFileSection(instance, instance.state, {
          requireFocus: false,
        }),
        table: shouldShowTableSection(instance, { requireFocus: false }),
      };
    },
  });

  // Reset exclusive mode when the selection no longer supports it.
  // Same-component setState-during-render (matches prior draft clearing).
  if (mode.type === "editLink" && !selection?.link) {
    setMode(IDLE);
  } else if (mode.type === "editFile" && !selection?.file) {
    setMode(IDLE);
  } else if (mode.type === "tableMenu" && !selection?.table) {
    setMode(IDLE);
  }

  const idle = () => setMode(IDLE);

  const startEditLink = (href: string) => {
    setMode({ type: "editLink", draft: href });
  };

  const setLinkDraft = (draft: string) => {
    setMode((prev) =>
      prev.type === "editLink" ? { type: "editLink", draft } : prev,
    );
  };

  const startEditFile = (name: string) => {
    setMode({ type: "editFile", draft: name });
  };

  const setFileDraft = (draft: string) => {
    setMode((prev) =>
      prev.type === "editFile" ? { type: "editFile", draft } : prev,
    );
  };

  const openTableMenu = () => {
    setMode({ type: "tableMenu" });
  };

  const closeTableMenu = () => {
    setMode((prev) => (prev.type === "tableMenu" ? IDLE : prev));
  };

  return (
    <FloatingToolbarContext.Provider
      value={{
        features,
        mode,
        idle,
        startEditLink,
        setLinkDraft,
        startEditFile,
        setFileDraft,
        openTableMenu,
        closeTableMenu,
      }}
    >
      {children}
    </FloatingToolbarContext.Provider>
  );
};

export const useFloatingToolbar = () => {
  const ctx = useContext(FloatingToolbarContext);
  if (!ctx) {
    throw new Error(
      "useFloatingToolbar must be used within FloatingToolbarProvider",
    );
  }
  return ctx;
};
